import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCompanyProfile } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ScoreStatCard from './ScoreStatCard';
import ScoreDistributionChart from './ScoreDistributionChart';
import usePageMeta from '../hooks/usePageMeta';
import '../styles/company.css';

const buildFallbackCompanyProfile = (rawName = '') => {
  const normalizedName = String(rawName || '').trim() || 'unknown-company';

  return {
    normalized_name: normalizedName,
    domain: 'N/A',
    description: 'Profile data is temporarily unavailable while we finalize data synchronization. Please refresh shortly for full details.',
    industry: 'N/A',
    country: null,
    onboarded: false,
    verification_level: 'not_verified',
    transparency: null,
    final_score: null,
    penalty_policy: null,
    penalty_vendor_network: null,
    penalty_outside_in: null,
    final_score_percentile: null,
    external_integrity_percentile: null,
    policy_compliance_percentile: null,
    vendor_summary: {
      vendor_band: '50_or_less',
      transparency_percentage: null,
    },
    latest_outside_in_scan: null,
    latest_policy_scan: null,
  };
};

const formatCompanyNameForDisplay = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return 'Unknown Company';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};

const getTopPercentLabel = (percentile) => {
  if (percentile == null || Number.isNaN(Number(percentile))) return 'N/A';
  const topPercent = Math.max(1, 101 - Math.round(Number(percentile)));
  return `TOP ${topPercent}%`;
};

const SCORE_HELP_TEXT = {
  securityScore: 'Total Security Score is a composite 0-100 score combining Policy Compliance, External Integrity, and Transparency. The four score circles show the sub-scores that together produce the Total Security Score, which reflects policy quality, external technical posture, and inherited ecosystem exposure.',
  transparency: 'Percentage of this company\'s declared vendors that have declared their own vendors, which affects confidence in the score.',
  externalIntegrity: 'Measures externally observable controls such as DNS hygiene, TLS posture, email authentication, and recent exposure risk.',
  policyCompliance: 'Measures how well policy evidence and governance signals align with expected security controls and documentation.',
};

const formatCount = (value) => {
  if (value == null || Number.isNaN(Number(value))) return 'N/A';
  return Number(value).toLocaleString();
};

const formatPercent = (value) => {
  if (value == null || Number.isNaN(Number(value))) return 'N/A';
  return `${Math.round(Number(value))}%`;
};

const formatYesNo = (value) => {
  if (value == null) return 'N/A';
  return value ? 'Pass' : 'Fail';
};

const buildScanRow = (criteria, result, effect) => ({ criteria, result, effect });

const formatScanStatusWithSuffix = (value, suffix) => {
  const status = formatYesNo(value);
  return status === 'N/A' ? 'N/A' : `${status}${suffix}`;
};

const formatOpenPortsResult = (value) => {
  const count = formatCount(value);
  if (count === 'N/A') return 'N/A';
  const numericCount = Number(value);
  return `${count} risky public port${numericCount === 1 ? '' : 's'}`;
};

const formatEmailDnsResult = (spfValue, dmarcValue, dnssecValue) => {
  if (spfValue == null || dmarcValue == null || dnssecValue == null) return 'N/A';
  return `${formatYesNo(spfValue)} SPF, ${formatYesNo(dmarcValue)} DMARC, ${formatYesNo(dnssecValue)} DNSSEC`;
};

const formatCveResult = (value) => {
  const count = formatCount(value);
  if (count === 'N/A') return 'N/A';
  const numericCount = Number(value);
  return `${count} match${numericCount === 1 ? '' : 'es'} detected`;
};

const formatPolicyScore = (value) => {
  if (value == null || Number.isNaN(Number(value))) return 'N/A';
  return `${Math.round(Number(value))}/20`;
};

const formatPolicyPenalty = (value) => {
  if (value == null || Number.isNaN(Number(value))) return 'No penalty recorded';
  return `-${Math.round(Number(value))} applied`;
};

const formatVendorBand = (value) => {
  if (!value) return 'N/A';
  if (value === 'more_than_50') return 'More than 50';
  if (value === '50_or_less') return '50 or fewer';
  return 'N/A';
};

function CompanySearch() {
  const { name } = useParams();
  const navigate = useNavigate();
  const { loginWithRedirect } = useAuth0();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Loading company information...');
  const [shellLoading, setShellLoading] = useState(false);
  const [displayedScore, setDisplayedScore] = useState(0);
  const [displayedTransparency, setDisplayedTransparency] = useState(0);
  const [displayedPenaltyOutside, setDisplayedPenaltyOutside] = useState(0);
  const [displayedPenaltyPolicy, setDisplayedPenaltyPolicy] = useState(0);

  const companyDisplayName = company ? formatCompanyNameForDisplay(company.normalized_name) : 'Company Profile';
  const securityScoreValueForTitle = company?.final_score == null ? 'N/A' : `${Math.round(Number(company.final_score))}/100`;

  usePageMeta({
    title: company
      ? `${companyDisplayName} - ${securityScoreValueForTitle}`
      : 'Company Profile',
    description: company
      ? `Public profile for ${companyDisplayName} with a public Total Security Score of ${securityScoreValueForTitle}.`
      : 'View company cybersecurity profile details and risk indicators on Risqly.',
  });

  React.useEffect(() => {
    const BACKGROUND_REFETCH_DELAY_MS = 3000;
    let isActive = true;
    let retryTimer = null;

    const runBackgroundRefetch = async (attempt) => {
      if (!isActive) return;

      setLoadingMessage('Synchronizing live profile data... validating current records.');

      try {
        const refreshed = await getCompanyProfile(name, { strategy: 'background' });
        if (!isActive) return;
        setCompany(refreshed);
        setShellLoading(false);
      } catch (backgroundError) {
        if (!isActive) return;
        retryTimer = setTimeout(() => {
          runBackgroundRefetch(attempt + 1);
        }, BACKGROUND_REFETCH_DELAY_MS);
      }
    };

    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    setLoading(true);
    setCompany(null);
    setShellLoading(false);
    setLoadingMessage('Loading company information...');
    const timer = setTimeout(() => {
      getCompanyProfile(name, {
        onRetry: () => {
          setShellLoading(true);
          setCompany((existing) => existing || buildFallbackCompanyProfile(name));
          setLoading(false);
          setLoadingMessage('Initializing secure data sync... retrying shortly.');
        },
      })
        .then((data) => {
          setCompany(data);
          setShellLoading(false);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Error loading company profile:', err);
          setCompany(buildFallbackCompanyProfile(name));
          setShellLoading(true);
          runBackgroundRefetch(1);
          setLoading(false);
        });
    }, 300);

    return () => {
      isActive = false;
      clearTimeout(timer);
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
    };
  }, [name]);

  // Animated score effect
  useEffect(() => {
    if (!company) return;
    
    let startValue = 0;
    const endValue = company.final_score ?? 0;
    const duration = 1200;
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const current = company.final_score == null ? null : Math.floor(startValue + (endValue - startValue) * progress);
      setDisplayedScore(current);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    animate();
  }, [company]);

  const handleClaimOrganization = () => {
    loginWithRedirect();
  };

  const vendorSummary = company?.vendor_summary || { vendor_band: '50_or_less', transparency_percentage: null };
  const latestOutsideInScan = company?.latest_outside_in_scan || null;
  const latestPolicyScan = company?.latest_policy_scan || null;
  const transparencyPercentage = vendorSummary?.transparency_percentage ?? null;
  const scoreDistributions = company?.score_distributions || {};
  const securityScoreValue = company?.final_score ?? null;
  const externalIntegrityValue = company?.penalty_outside_in == null ? null : 100 - company.penalty_outside_in;
  const policyComplianceValue = company?.penalty_policy == null ? null : 100 - company.penalty_policy;
  const hasExternalIntegrityData = Boolean(
    latestOutsideInScan && [
      latestOutsideInScan.website_encryption,
      latestOutsideInScan.tls_valid,
      latestOutsideInScan.secure_headers,
      latestOutsideInScan.open_ports_count,
      latestOutsideInScan.spf_valid,
      latestOutsideInScan.dmarc_valid,
      latestOutsideInScan.dnssec_enabled,
      latestOutsideInScan.tls_version,
      latestOutsideInScan.cve_match_count,
      latestOutsideInScan.cve_risk_signal,
      latestOutsideInScan.cve_penalty,
      latestOutsideInScan.raw_score,
      latestOutsideInScan.penalty,
      latestOutsideInScan.scanned_at,
    ].some((value) => value != null)
  );
  const hasPolicyScanData = Boolean(
    latestPolicyScan && [
      latestPolicyScan.frameworks_score,
      latestPolicyScan.coverage_score,
      latestPolicyScan.specificity_score,
      latestPolicyScan.governance_score,
      latestPolicyScan.execution_score,
      latestPolicyScan.penalty,
      latestPolicyScan.analyzed_at,
    ].some((value) => value != null)
  );

  // Animated transparency effect
  useEffect(() => {
    if (!company) return;
    
    let startValue = 0;
    const endValue = (company.transparency ?? 0) * 100;
    const duration = 1200;
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const current = company.transparency == null ? null : Math.floor(startValue + (endValue - startValue) * progress);
      setDisplayedTransparency(current);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    animate();
  }, [company]);

  const vendorEvidenceRows = [
    buildScanRow(
      'Vendor network size',
      formatVendorBand(vendorSummary.vendor_band),
      'Risqly shows a general size band instead of exact vendor totals to protect private relationship details.'
    ),
    buildScanRow(
      'Transparency',
      transparencyPercentage == null ? 'N/A' : formatPercent(transparencyPercentage),
      'Transparency reflects the share of vendors that are themselves onboarded.'
    ),
    buildScanRow(
      'Vendor-network penalty',
      company?.penalty_vendor_network == null ? 'Not yet scored' : `-${company.penalty_vendor_network} applied`,
      'This propagated penalty is what affects the total score when vendor exposure is inherited through the network.'
    ),
  ];

  const externalEvidenceRows = hasExternalIntegrityData
    ? [
        buildScanRow(
          'Website delivery',
          formatYesNo(latestOutsideInScan.website_encryption),
          'Website delivery contributes to the website portion of the external integrity penalty.'
        ),
        buildScanRow(
          'TLS posture',
          formatScanStatusWithSuffix(latestOutsideInScan.tls_valid, latestOutsideInScan.tls_version ? `, ${latestOutsideInScan.tls_version}` : ''),
          'TLS validity and protocol strength affect the transport portion of the external integrity penalty.'
        ),
        buildScanRow(
          'Secure headers',
          formatYesNo(latestOutsideInScan.secure_headers),
          'Headers such as CSP and HSTS reduce the website hardening penalty.'
        ),
        buildScanRow(
          'Open ports',
          formatOpenPortsResult(latestOutsideInScan.open_ports_count),
          'Each risky exposed port adds network penalty points, capped at the network slice maximum.'
        ),
        buildScanRow(
          'Email and DNS trust',
          formatEmailDnsResult(latestOutsideInScan.spf_valid, latestOutsideInScan.dmarc_valid, latestOutsideInScan.dnssec_enabled),
          'Email authentication and DNS integrity affect the email and DNS portions of the external integrity penalty.'
        ),
        buildScanRow(
          'CVE signal',
          formatCveResult(latestOutsideInScan.cve_match_count),
          'Recent CVE matches contribute a capped vulnerability penalty.'
        ),
      ]
    : [
        buildScanRow(
          'Website delivery',
          'N/A',
          'N/A'
        ),
        buildScanRow(
          'TLS posture',
          'N/A',
          'N/A'
        ),
        buildScanRow(
          'Secure headers',
          'N/A',
          'N/A'
        ),
        buildScanRow(
          'Open ports',
          'N/A',
          'N/A'
        ),
        buildScanRow(
          'Email and DNS trust',
          'N/A',
          'N/A'
        ),
        buildScanRow(
          'CVE signal',
          'N/A',
          'N/A'
        ),
      ];

  const policyEvidenceRows = hasPolicyScanData
    ? [
        buildScanRow(
          'Framework coverage',
          formatPolicyScore(latestPolicyScan.frameworks_score),
          'Checks whether the policy references a defined security framework or control baseline.'
        ),
        buildScanRow(
          'Control coverage',
          formatPolicyScore(latestPolicyScan.coverage_score),
          'Checks whether core control areas such as access, patching, and incident response are covered.'
        ),
        buildScanRow(
          'Specificity',
          formatPolicyScore(latestPolicyScan.specificity_score),
          'Checks whether the policy contains concrete, testable statements instead of generic language.'
        ),
        buildScanRow(
          'Governance',
          formatPolicyScore(latestPolicyScan.governance_score),
          'Checks whether ownership, review cadence, and accountability are clearly defined.'
        ),
        buildScanRow(
          'Execution',
          formatPolicyScore(latestPolicyScan.execution_score),
          'Checks whether the policy is operationalized with evidence, procedures, or implemented controls.'
        ),
        buildScanRow(
          'Policy penalty',
          formatPolicyPenalty(latestPolicyScan.penalty ?? company?.penalty_policy),
          'This policy penalty contributes to the policy compliance score.'
        ),
      ]
    : [
        buildScanRow(
          'Framework coverage',
          'N/A',
          'Checks whether the policy references a defined security framework or control baseline.'
        ),
        buildScanRow(
          'Control coverage',
          'N/A',
          'Checks whether core control areas such as access, patching, and incident response are covered.'
        ),
        buildScanRow(
          'Specificity',
          'N/A',
          'Checks whether the policy contains concrete, testable statements instead of generic language.'
        ),
        buildScanRow(
          'Governance',
          'N/A',
          'Checks whether ownership, review cadence, and accountability are clearly defined.'
        ),
        buildScanRow(
          'Execution',
          'N/A',
          'Checks whether the policy is operationalized with evidence, procedures, or implemented controls.'
        ),
      ];

  const transparencyEvidenceRows = [
    buildScanRow(
      'Vendor onboarding ratio',
      transparencyPercentage == null ? 'N/A' : formatPercent(transparencyPercentage),
      'Shows how much of the vendor network is visible through onboarded relationships.'
    ),
    buildScanRow(
      'Public confidence impact',
      transparencyPercentage == null ? 'N/A' : formatPercent(transparencyPercentage),
      'Higher transparency generally increases confidence because more of the graph is observed directly.'
    ),
  ];

  // Animated penalty outside effect
  useEffect(() => {
    if (!company) return;
    
    let startValue = 0;
    const endValue = 100 - (company.penalty_outside_in ?? 0);
    const duration = 1200;
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const current = company.penalty_outside_in == null ? null : Math.floor(startValue + (endValue - startValue) * progress);
      setDisplayedPenaltyOutside(current);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    animate();
  }, [company]);

  // Animated penalty policy effect
  useEffect(() => {
    if (!company) return;
    
    let startValue = 0;
    const endValue = 100 - (company.penalty_policy ?? 0);
    const duration = 1200;
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const current = company.penalty_policy == null ? null : Math.floor(startValue + (endValue - startValue) * progress);
      setDisplayedPenaltyPolicy(current);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    animate();
  }, [company]);

  return (
    <div className={`company-page ${shellLoading ? 'company-page--shell' : ''}`.trim()}>
      <Header />
      
      {loading ? (
        <div className="company-loading-container">
          <p>{loadingMessage}</p>
        </div>
      ) : company ? (
        <main className={`company-main ${shellLoading ? 'company-main--shell' : ''}`.trim()} style={{ animation: 'fadeIn 0.6s ease-out' }}>
          {shellLoading && (
            <div className="company-shell-banner" role="status" aria-live="polite">
              <span className="company-shell-dot" />
              <span>{loadingMessage}</span>
            </div>
          )}

          {!company.onboarded && !shellLoading && (
            <div className="company-claim-box company-claim-box--compact" style={{ animation: 'fadeInUp 0.6s ease-out' }}>
              <h3>Is this your organization?</h3>
              <p>
                Register to claim this profile, complete onboarding, and unlock your verified Risqly security score.
              </p>
              <button className="btn btn-primary" onClick={handleClaimOrganization}>
                Register to Claim
              </button>
            </div>
          )}

          <div className="company-header" style={{ animation: 'fadeInUp 0.6s ease-out' }}>
            <div className="header-content">
              <div className="badges" />
              <h1 className="company-name">
                {companyDisplayName}
              </h1>
              <h1 className="company-domain">{company.domain}</h1>
              <h1 className="company-industry">Industry: {company.industry || "Unknown"}</h1>
              <p className="company-description">{company.description || "No description available."}</p>
            </div>
            <div className="ranking-badges">
              <div className="ranking-badge">
                <span className="ranking-label">Verification Level</span>
                <span className="ranking-value" style={{
                  color: company.verification_level === 'fully_verified' 
                    ? '#00c853' 
                    : company.verification_level === 'stage_1_verified' 
                    ? '#fbc02d' 
                    : '#e53935'
                }}>
                  {company.verification_level === 'fully_verified'
                    ? 'FULLY VERIFIED'
                    : company.verification_level === 'stage_1_verified'
                    ? 'STAGE 1 VERIFIED'
                    : 'NOT VERIFIED'}
                </span>
              </div>
                <div className="ranking-badge">
                  <span className="ranking-label">Security Percentile</span>
                  <span className="ranking-value">{getTopPercentLabel(company.final_score_percentile)}</span>
                </div>
            </div>
          </div>

          <div className="scores-grid">
            <ScoreStatCard
              className={`profile-score-card ${shellLoading ? 'profile-score-card--shell' : ''}`.trim()}
              title="Total Security Score"
              description="Combined signal showing present resilience across policy quality, external technical posture, and ecosystem exposure."
              infoText={SCORE_HELP_TEXT.securityScore}
              value={displayedScore}
              ringValue={displayedScore}
              color="#00ff41"
            />

            <ScoreStatCard
              className={`profile-score-card ${shellLoading ? 'profile-score-card--shell' : ''}`.trim()}
              title="Transparency"
              description="Percentage of this company's declared vendors that have declared their own vendors, improving the validity and confidence of the final score."
              infoText={SCORE_HELP_TEXT.transparency}
              value={displayedTransparency}
              ringValue={displayedTransparency}
              color="#ff9500"
              suffix="%"
              showMax={false}
            />

            <ScoreStatCard
              className={`profile-score-card ${shellLoading ? 'profile-score-card--shell' : ''}`.trim()}
              title="External Integrity"
              description="Publicly observable controls such as DNS hygiene, TLS posture, email authentication quality, and recent CVE risk signals."
              infoText={SCORE_HELP_TEXT.externalIntegrity}
              value={displayedPenaltyOutside}
              ringValue={displayedPenaltyOutside}
              color="#e91e63"
            />

            <ScoreStatCard
              className={`profile-score-card ${shellLoading ? 'profile-score-card--shell' : ''}`.trim()}
              title="Policy Compliance"
              description="Strength and operational clarity of policy evidence against common security governance expectations."
              infoText={SCORE_HELP_TEXT.policyCompliance}
              value={displayedPenaltyPolicy}
              ringValue={displayedPenaltyPolicy}
              color="#00bcd4"
            />
          </div>

          <section className="evidence-section" aria-label="What drives the public score">
            <div className="evidence-section__header">
              <h2>Details</h2>
              <p>Public evidence behind the score circles.</p>
            </div>

            <div className="evidence-grid">
              <article className="evidence-card">
                <h3>Vendors</h3>
                <p className="evidence-card__intro">Declared vendor relationships and the network penalty they create.</p>
                <div className="evidence-table" role="list">
                  {vendorEvidenceRows.map((row) => (
                    <div className="evidence-row" role="listitem" key={`${row.criteria}-${row.result}`}>
                      <div className="evidence-row__criteria">{row.criteria}</div>
                      <div className="evidence-row__result">{row.result}</div>
                      <div className="evidence-row__effect">{row.effect}</div>
                    </div>
                  ))}
                </div>
              </article>

              <article className="evidence-card">
                <h3>External Scan</h3>
                <p className="evidence-card__intro">Public technical checks from the latest outside-in scan.</p>
                <div className="evidence-table" role="list">
                  {externalEvidenceRows.map((row) => (
                    <div className="evidence-row" role="listitem" key={`${row.criteria}-${row.result}`}>
                      <div className="evidence-row__criteria">{row.criteria}</div>
                      <div className="evidence-row__result">{row.result}</div>
                      <div className="evidence-row__effect">{row.effect}</div>
                    </div>
                  ))}
                </div>
              </article>

              <article className="evidence-card">
                <h3>Policy</h3>
                <p className="evidence-card__intro">Policy evidence is evaluated for structure, coverage, and operational clarity.</p>
                <div className="evidence-table" role="list">
                  {policyEvidenceRows.map((row) => (
                    <div className="evidence-row" role="listitem" key={`${row.criteria}-${row.result}`}>
                      <div className="evidence-row__criteria">{row.criteria}</div>
                      <div className="evidence-row__result">{row.result}</div>
                      <div className="evidence-row__effect">{row.effect}</div>
                    </div>
                  ))}
                </div>
              </article>

              <article className="evidence-card evidence-card--transparency">
                <h3>Transparency</h3>
                <p className="evidence-card__intro">How much of the vendor graph is visible from onboarded relationships.</p>
                <div className="evidence-table" role="list">
                  {transparencyEvidenceRows.map((row) => (
                    <div className="evidence-row" role="listitem" key={`${row.criteria}-${row.result}`}>
                      <div className="evidence-row__criteria">{row.criteria}</div>
                      <div className="evidence-row__result">{row.result}</div>
                      <div className="evidence-row__effect">{row.effect}</div>
                    </div>
                  ))}
                </div>
              </article>
            </div>
          </section>

          <section className="distribution-section" aria-label="Total Security Score distributions">
            <h2 className="distribution-section__title">Network Score Distribution</h2>
            <div className="distribution-grid">
              <ScoreDistributionChart
                title="Total Security Score"
                description="How your Total Security Score compares across all scored companies in the network."
                infoText={SCORE_HELP_TEXT.securityScore}
                score={securityScoreValue}
                percentile={company.final_score_percentile}
                buckets={scoreDistributions.security_score}
                accentColor="#a0ffc3"
              />

              <ScoreDistributionChart
                title="External Integrity"
                description="Distribution of externally observable posture across the full company network."
                infoText={SCORE_HELP_TEXT.externalIntegrity}
                score={externalIntegrityValue}
                percentile={company.external_integrity_percentile}
                buckets={scoreDistributions.external_integrity}
                accentColor="#f58ab5"
              />

              <ScoreDistributionChart
                title="Policy Compliance"
                description="Distribution of policy evidence quality and governance readiness over all companies."
                infoText={SCORE_HELP_TEXT.policyCompliance}
                score={policyComplianceValue}
                percentile={company.policy_compliance_percentile}
                buckets={scoreDistributions.policy_compliance}
                accentColor="#7fe8ff"
              />
            </div>
          </section>
        </main>
      ) : (
        <div className="not-found" style={{ animation: 'fadeIn 0.6s ease-out' }}>
          <h2>Company Not Found</h2>
          <p>This company may not be registered in the Risqly network.</p>
          <div className="company-claim-box">
            <h3>Is this your organization?</h3>
            <p>Register to claim this organization and get your first Risqly security score.</p>
            <button className="btn btn-primary" onClick={handleClaimOrganization}>
              Register to Claim
            </button>
          </div>
          <button className="btn btn-primary" onClick={() => navigate('/')}>
            Return to Home
          </button>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default CompanySearch;
